import styled from '@emotion/styled'

import { WebLink, WebLinkProps } from 'common/navigation/web-link'

import { conditionalStyles } from 'component-library/__styling/conditional-styles'

import { transition } from 'component-library/__styling/style-mixins'
import { headlineBoxshadow } from 'component-library/__styling/mixins/headline-boxshadow'
import { colors } from 'component-library/__styling/settings'

import {
    styledMediaClassName,
    styledPlayButtonClassName,
    styledGalleryButtonClassName,
} from 'component-library/cards/CardMedia/StyledMedia.styled'

import { styledHeadlineTextClassName } from 'component-library/cards/CardText/CardText.styled'
import { CSSObject } from '@emotion/css'
import { styledCardTimestampTextLabel } from 'component-library/content/CardTimestamp/CardTimestamp.styled'
import { themedValue } from 'component-library/__styling/themed-value'
import { playButtonStates } from 'component-library/buttons/PlayButton/PlayButton.styled'
import { FillProperty } from 'csstype'
import { styledWrapperClassName } from '../LandscapeWide/Styled/StyledText.styled'
import { createPropFilter } from 'common/emotion'

export interface StyledPortraitCardProps {
    isSponsored: boolean | null
    hasBackground?: boolean
    primaryTopic: string | undefined
    hasPadding?: boolean
}

// Interaction Styles for Cards on PerthNow
export const cardInteractionFocusStylesPerthNow: CSSObject = {
    '&:focus': {
        outline: 'none',

        [`& .${styledHeadlineTextClassName}`]: {
            textDecoration: 'underline',
        },

        [`& .${styledMediaClassName}, & + .${styledWrapperClassName} .${styledMediaClassName}`]: {
            '&::after': {
                borderColor: colors.perthnow.greyCoal,
            },
        },
    },
}

export const CardInteractionHoverStylesPerthNow: CSSObject = {
    '&:hover': {
        [`& .${styledHeadlineTextClassName}`]: {
            color: colors.perthnow.blueKyanite,
        },

        [`& .${styledMediaClassName}`]: {
            '&::after': {
                height: '100%',
                borderColor: colors.white50,
            },
        },
    },
}

export const CardInteractionPlayButtonStylesPerthNow: CSSObject = {
    '&:hover, &:focus': {
        [`& .${styledPlayButtonClassName}`]: {
            backgroundColor: colors.perthnow.pinkThulite,

            '&::before': {
                borderLeftColor: colors.white, // Play button CSS color, uses border to create the triangle
            },
        },
    },
}

export interface StyledLinkProps {
    hasBackground?: boolean
    isLoading?: boolean
    isLarge?: boolean
}

export const StyledLink = styled(WebLink, {
    shouldForwardProp: createPropFilter<WebLinkProps>()([
        'hasBackground',
        'isLoading',
        'isLarge',
        'section',
    ]),
})<StyledLinkProps>(
    {
        textDecoration: 'none',
        textDecorationSkipInk: 'auto',
        ...transition({ properties: ['color'] }),
    },
    ({ theme }) => ({
        color: themedValue(theme, { fallback: colors.black, perthnow: colors.perthnow.greyCoal }),
        '&:hover, &:focus': {
            [`& .${styledGalleryButtonClassName}`]: {
                backgroundColor: themedValue(theme, {
                    thewest: section => section.primaryColor,
                    perthnow: colors.perthnow.pinkThulite,
                    fallback: colors.thewest.westblue,
                }),
            },
        },
    }),
    ({ theme, hasBackground, isLoading }) => ({
        ...conditionalStyles(isLoading, {
            cursor: 'default',
        }),

        // Interactions on Cards - PerthNow
        ...conditionalStyles(theme.kind === 'perthnow', {
            ...cardInteractionFocusStylesPerthNow,
            ...CardInteractionHoverStylesPerthNow,
            ...CardInteractionPlayButtonStylesPerthNow,
        }),

        // PerthNow only for cards with a background colour
        ...conditionalStyles(theme.kind === 'perthnow' && hasBackground, {
            color: colors.white,

            '&:hover': {
                [`& .${styledHeadlineTextClassName}`]: {
                    ...headlineBoxshadow,
                    color: colors.white,
                    background: colors.perthnow.blueSapphire,
                },
            },
        }),

        // Interactions on Card Image - The West
        ...conditionalStyles(theme.kind === 'thewest', {
            [`& .${styledMediaClassName}`]: {
                '&::after': {
                    height: '100%',
                    borderWidth: '0 0 4px 0',
                    borderColor: 'transparent',
                },
            },

            '&:focus': {
                outline: 'none',

                [`& .${styledHeadlineTextClassName}`]: {
                    textDecoration: 'underline',
                },

                [`& .${styledMediaClassName}`]: {
                    '&::after': {
                        borderColor: colors.thewest.greyGorilla,
                    },
                },

                [`& .Kicker`]: {
                    textDecoration: 'underline',
                    color: colors.black,
                },
            },

            '&:hover': {
                [`& .${styledHeadlineTextClassName}`]: {
                    color: theme.sectionValues.primaryColor,
                },

                [`& .${styledMediaClassName}`]: {
                    '&::after': {
                        borderColor: theme.sectionValues.primaryColor,
                    },
                },

                [`& .Kicker svg`]: {
                    fill: theme.sectionValues.primaryColor,
                },
            },
        }),

        // Interactions on Card Image - Sevennews
        ...conditionalStyles(theme.kind === 'sevennews', {
            ...playButtonStates(theme),

            [`& .${styledMediaClassName}`]: {
                '&::after': {
                    borderWidth: '0 0 4px 0',
                    borderColor: 'transparent',
                },
            },

            '&:hover': {
                [`& .${styledMediaClassName}`]: {
                    '&::before': {
                        height: '120%',
                    },
                },
                [`& .${styledHeadlineTextClassName}`]: {
                    color: themedValue(theme, {
                        sevennews: section =>
                            hasBackground ? undefined : section.cards.headline.hoverColor,
                        fallback: undefined,
                    }),
                    textDecoration: 'underline',
                },
                [`& .Kicker svg`]: {
                    fill: themedValue(theme, {
                        sevennews: section =>
                            hasBackground ? undefined : section.cards.kicker.hoverColor,
                        fallback: undefined,
                    }) as FillProperty,
                },
                [`& .${styledCardTimestampTextLabel}`]: {
                    color: themedValue(theme, {
                        sevennews: section => section.cards.timestamp.hoverColor,
                        fallback: undefined,
                    }),
                },
            },

            '&:focus': {
                outline: `solid 2px`,
                outlineColor: themedValue(theme, {
                    sevennews: section => section.focusColor,
                    fallback: undefined,
                }),
            },

            // to prevent flash on desktop when clicking through
            '&:active': {
                outline: themedValue(theme, { sevennews: 'none', fallback: undefined }),
            },
        }),
    })
)
